import { useCallback } from "react";
import { ReportColumn, Report as ReportType } from "/app/src/models";
import { useQueryClient } from "@tanstack/react-query";
import { reportService } from "/app/src/services";
import { Formik, FormikProps } from "formik";
import { Col, Row } from "antd";
import { Form, Select, SubmitButton } from "formik-antd";
import { useTranslation } from "react-i18next";

interface FormValues {
  groupColumnId: number | undefined;
  subGroupColumnId: number | undefined;
  finalGroupColumnId: number | undefined;
  groupRange?: "minute" | "hour" | "day" | "week" | "month" | "year";
}

/**
 * Component to set the grouping columns for the report
 */
export default function GroupingColumns({
  report,
  columns,
}: {
  report: ReportType;
  columns: ReportColumn[];
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const onSubmitHandler = useCallback(
    (values: FormValues) => {
      return reportService
        .updateSingle(report.id, {
          groupColumnId: values.groupColumnId ? values.groupColumnId : null,
          subGroupColumnId: values.subGroupColumnId
            ? values.subGroupColumnId
            : null,
          finalGroupColumnId: values.finalGroupColumnId
            ? values.finalGroupColumnId
            : null,
          groupRange: values.groupRange,
        })
        .then(() => {
          queryClient.setQueryData(["report", report.id], {
            report: {
              ...report,
              groupColumnId: values.groupColumnId,
              subGroupColumnId: values.subGroupColumnId,
              finalGroupColumnId: values.finalGroupColumnId,
              groupRange: values.groupRange,
            },
          });
        });
    },
    [report, queryClient],
  );

  const anyGroupedDateTimeColumn = useCallback(
    (values: FormValues) =>
      [
        values.groupColumnId,
        values.subGroupColumnId,
        values.finalGroupColumnId,
      ].some((columnId) =>
        columns.some(
          (column) =>
            column.id === columnId && column.filterType === "DateTime",
        ),
      ),
    [columns],
  );

  const columnForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ values, isSubmitting, dirty }) => (
        <Form>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="groupColumnId" hasFeedback={false}>
                <Select
                  size="large"
                  name="groupColumnId"
                  placeholder={t("translation:select_group_column")}
                  allowClear
                >
                  {columns
                    .filter(
                      (column) =>
                        column.id !== values.subGroupColumnId &&
                        column.id !== values.finalGroupColumnId &&
                        column.type !== "count",
                    )
                    .map((column) => (
                      <Select.Option key={column.id} value={column.id}>
                        {column.name}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="subGroupColumnId" hasFeedback={false}>
                <Select
                  size="large"
                  name="subGroupColumnId"
                  placeholder={t("translation:select_sub_group_column")}
                  allowClear
                >
                  {columns
                    .filter(
                      (column) =>
                        column.id !== values.groupColumnId &&
                        column.id !== values.finalGroupColumnId &&
                        column.type !== "count",
                    )
                    .map((column) => (
                      <Select.Option key={column.id} value={column.id}>
                        {column.name}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="finalGroupColumnId" hasFeedback={false}>
                <Select
                  size="large"
                  name="finalGroupColumnId"
                  placeholder={t("translation:select_final_group_column")}
                  allowClear
                >
                  {columns
                    .filter(
                      (column) =>
                        column.id !== values.groupColumnId &&
                        column.id !== values.subGroupColumnId &&
                        column.type !== "count",
                    )
                    .map((column) => (
                      <Select.Option key={column.id} value={column.id}>
                        {column.name}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item name="groupRange">
                {anyGroupedDateTimeColumn(values) && (
                  <Select
                    name="groupRange"
                    size="large"
                    placeholder={t("translation:grouping_range")}
                  >
                    <Select.Option key="1" value="minute">
                      {t("translation:minute")}
                    </Select.Option>
                    <Select.Option key="2" value="hour">
                      {t("translation:hour")}
                    </Select.Option>
                    <Select.Option key="3" value="day">
                      {t("translation:day")}
                    </Select.Option>
                    <Select.Option key="4" value="week">
                      {t("translation:week")}
                    </Select.Option>
                    <Select.Option key="5" value="month">
                      {t("translation:month")}
                    </Select.Option>
                    <Select.Option key="6" value="year">
                      {t("translation:year")}
                    </Select.Option>
                  </Select>
                )}
              </Form.Item>
            </Col>
            <Col span={3}>
              <SubmitButton
                type="primary"
                size="large"
                block
                disabled={!dirty || isSubmitting}
              >
                {t("translation:save")}
              </SubmitButton>
            </Col>
          </Row>
        </Form>
      ),
      [anyGroupedDateTimeColumn, columns, t],
    );

  return (
    <div className="groupingColumns">
      <h2>{t("translation:column_grouping")}</h2>
      <Formik
        component={columnForm}
        enableReinitialize
        initialValues={{
          groupColumnId: report.groupColumnId,
          subGroupColumnId: report.subGroupColumnId,
          finalGroupColumnId: report.finalGroupColumnId,
          groupRange: report.groupRange,
        }}
        onSubmit={onSubmitHandler}
      />
    </div>
  );
}
